import React, { useState, useRef, useEffect } from "react";
import "../../../styles/Part3.css";
import ConversationQuestion from "./ConversationQuestion";
import { storageQuestions } from "../../../data/part3/storage";
import VocabularyPractice from "./VocabularyPractice";
import { personnelQuestions } from "../../../data/part3/personnel";
import { businessQuestions } from "../../../data/part3/business";
import { financeQuestions } from "../../../data/part3/finance";
import { leisureQuestions } from "../../../data/part3/leisure";
import { dailyQuestions } from "../../../data/part3/daily";
import { db, auth } from "../../../firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { useSubscriptionStatus } from "../../../hooks/useSubscriptionStatus";
import { freeContent } from "../../../utils/freeContent";

const Part3_1 = ({ isActive }) => {
  const { isActive: useSubscriptionStatusIsActive } = useSubscriptionStatus();
  const [selectedCategory, setSelectedCategory] = useState("");
  const [showQuestions, setShowQuestions] = useState(false);
  const [currentConversation, setCurrentConversation] = useState(1);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [accentType, setAccentType] = useState("us");
  const [isStarted, setIsStarted] = useState(false);
  const audioRef = useRef(null);
  const [showVocabularyPractice, setShowVocabularyPractice] = useState(false);
  const [completedCategories, setCompletedCategories] = useState({});

  useEffect(() => {
    // 완료된 카테고리 불러오기
    const loadCompletedCategories = async () => {
      if (!auth.currentUser) return;

      const userRef = doc(db, "users", auth.currentUser.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const data = userDoc.data();
        setCompletedCategories(data.part3CompletedCategories || {});
      }
    };

    loadCompletedCategories();
  }, []);

  const handleComplete = async (categoryKey) => {
    if (!auth.currentUser) {
      return;
    }

    try {
      const updatedCategories = {
        ...completedCategories,
        [categoryKey]: true,
      };

      // Firestore에 저장
      const userRef = doc(db, "users", auth.currentUser.uid);
      await setDoc(
        userRef,
        {
          part3CompletedCategories: updatedCategories,
        },
        { merge: true }
      );

      setCompletedCategories(updatedCategories);
      setSelectedCategory("");
      setShowQuestions(false);
      setCurrentConversation(1);
    } catch (error) {
      console.error("학습 완료 처리 중 오류 발생:", error);
    }
  };

  const subCategories = {
    office: {
      title: "사무실 & 인사관리",
      description: "사무실 환경, 인사 관리, 직원 복지, 채용 관련 대화",
      examples: ["직원 채용", "근무 일정", "업무 평가"],
      questions: storageQuestions,
    },
    personnel: {
      title: "공장 & 재고 & 배송 & 산업",
      description: "공장 운영, 재고 관리, 물류 배송, 산업 현장 관련 대화",
      examples: ["재고 점검", "배송 일정", "생산 공정"],
      questions: personnelQuestions,
    },
    business: {
      title: "비즈니스 & 마케팅 & 영업 & 커뮤니케이션",
      description: "비즈니스 전략, 마케팅 계획, 영업 활동 관련 대화",
      examples: ["마케팅 전략", "영업 보고", "고객 관리"],
      questions: businessQuestions,
    },
    finance: {
      title: "재무 & 회계 & 투자 & 보험",
      description: "재무 관리, 회계 처리, 투자 계획, 보험 관련 대화",
      examples: ["예산 계획", "투자 상담", "보험 청구"],
      questions: financeQuestions,
    },
    leisure: {
      title: "여가 & 렌탈 & 교통 & 예약",
      description: "여가 활동, 렌탈 서비스, 교통, 예약 관련 대화",
      examples: ["여행 계획", "차량 대여", "호텔 예약"],
      questions: leisureQuestions,
    },
    daily: {
      title: "일상생활",
      description: "일상적인 대화와 상황",
      examples: ["일상 대화", "개인 생활", "취미 활동"],
      questions: dailyQuestions,
    },
  };

  const handleAnswer = (choice) => {
    // 답안 선택 처리
  };

  const handleNext = () => {
    setCurrentConversation((prev) => prev + 1);
  };

  const handlePrevious = () => {
    setCurrentConversation((prev) => prev - 1);
  };

  const handleBack = () => {
    setShowQuestions(false);
    setSelectedCategory(""); // 선택된 카테고리도 초기화
    setCurrentConversation(1); // 대화 번호도 초기화
  };

  const handleStart = () => {
    // 학습 시작
  };

  const handleCategorySelect = (category) => {
    if (category === "vocabulary") {
      setShowVocabularyPractice(true);
    } else {
      setSelectedCategory(category);
      setShowQuestions(true);
      setCurrentConversation(1);
    }
  };

  const getCurrentConversation = () => {
    return (
      selectedCategory &&
      subCategories[selectedCategory].questions[
        `conversation${currentConversation}`
      ]
    );
  };

  const hasNextConversation = () => {
    return (
      selectedCategory &&
      subCategories[selectedCategory].questions[
        `conversation${currentConversation + 1}`
      ] !== undefined
    );
  };

  const getTotalConversations = () => {
    if (!selectedCategory || !subCategories[selectedCategory].questions)
      return 0;
    return Object.keys(subCategories[selectedCategory].questions).filter(
      (key) => key.startsWith("conversation")
    ).length;
  };

  const renderSubCategorySelection = () => {
    const freeCategories = freeContent.part3.conversations || [];

    return (
      <div className="subcategory-container">
        {selectedCategory && (
          <button className="back-button" onClick={handleBack}>
            ← 학습 유형 선택으로 돌아가기
          </button>
        )}

        <h2>대화 선택</h2>
        <div className="subcategory-grid">
          {Object.entries(subCategories).map(([key, category]) => {
            const isFreeContent = freeCategories.includes(key);

            return (
              <div
                key={key}
                className="subcategory-card"
                onClick={() => handleSubCategoryClick(key)}
              >
                <h3>{category.title}</h3>
                <p>{category.description}</p>
                {completedCategories[key] && (
                  <div className="completion-badge">학습완료</div>
                )}
                {!isActive &&
                  !isFreeContent && ( // 구독자 아닐 때 + 유료컨텐츠면 -> 잠금표시
                    <div className="lock-overlay">
                      <img src="/icons/lock.svg" alt="잠김" />
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const handleSubCategoryClick = (key) => {
    const freeCategories = freeContent.part3.conversations || [];
    const isFreeContent = freeCategories.includes(key);

    // 구독자가 아니고 유료 컨텐츠면 클릭 막기
    if (!isActive && !isFreeContent) {
      return;
    }

    setSelectedCategory(key);
    setShowQuestions(true);
  };

  return (
    <div className="part3-container">
      {showVocabularyPractice ? (
        <VocabularyPractice onBack={() => setShowVocabularyPractice(false)} />
      ) : !showQuestions ? (
        renderSubCategorySelection()
      ) : (
        <div className="question-content">
          <ConversationQuestion
            conversation={
              selectedCategory &&
              subCategories[selectedCategory].questions[
                `conversation${currentConversation}`
              ]
            }
            accentType={accentType}
            onComplete={() => handleComplete(selectedCategory)}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onBack={handleBack}
            hasNext={
              selectedCategory &&
              subCategories[selectedCategory].questions[
                `conversation${currentConversation + 1}`
              ] !== undefined
            }
            currentIndex={currentConversation - 1}
            totalCount={getTotalConversations()}
          />
        </div>
      )}
    </div>
  );
};

export default Part3_1;
